import { uniqueId } from "lodash";

import { Guid } from "@/Common/Models/Guid";

import styles from "./TextRadioGroup.module.scss";

type TextRadioGroupValue = string | number | boolean | null;

/**
 * A selectable item shown in a {@link TextRadioGroup}.
 * @template TValue The option value type.
 */
interface ITextRadioGroupItem<TValue extends TextRadioGroupValue> {
    /**
     * The displayed option label.
     */
    label: string;

    /**
     * The option value.
     */
    value: TValue;

    /**
     * A callback to invoke when the option is selected.
     */
    onSelect?: () => Promise<void> | void;

    /**
     * Whether the option is disabled, so it can't be selected.
     */
    isDisabled?: boolean;
}

/**
 * The properties for a {@link TextRadioGroup} component.
 * @template TValue The option value type.
 */
interface ITextRadioGroupProps<TValue extends TextRadioGroupValue> {
    /**
     * The id for the fieldset and the key.
     */
    fieldsetId: Guid;

    /**
     * The accessible label of the radio group.
     */
    ariaLabel: string;

    /**
     * The options to show in the radio group.
     */
    items: ITextRadioGroupItem<TValue>[];

    /**
     * The selected value.
     */
    selectedValue?: TValue;

    /**
     * A callback to invoke when an option is selected. This receives the newly selected value.
     */
    onChange?: (selected: TValue) => void;

    /**
     * The HTML name for the radio button group.
     */
    name?: string;
}

/**
 * A component which shows a list of selectable radio buttons and labels.
 * @template TValue The option value type.
 * @param props The component properties.
 */
function TextRadioGroup<TValue extends TextRadioGroupValue>(props: Readonly<ITextRadioGroupProps<TValue>>): JSX.Element {
    const name = props.name ?? `text-radio-group-${uniqueId()}`;

    return (
        <fieldset id={props.fieldsetId} aria-label={props.ariaLabel} className={styles.textRadioGroup}>
            {props.items.map((item, index) => {
                const radioId = `text-radio-${uniqueId()}`;
                const isItemSelected = item.value === props.selectedValue;
                const isLast = props.items.length - 1 === index;

                return (
                    <span key={`${item.value}-${props.fieldsetId}`}>
                        <input
                            type="radio"
                            name={name}
                            value={item.label}
                            className={styles.hiddenAccessible}
                            id={radioId}
                            checked={isItemSelected}
                            onChange={() => {
                                if (isItemSelected)
                                    return;

                                props.onChange?.(item.value);
                                item.onSelect?.();
                            }}
                        />
                        <label
                            htmlFor={radioId}
                            className={`${styles.textRadio} ${isItemSelected ? styles.active : ""}`}
                        >
                            {item.label}
                        </label>
                        {!isLast && <span className={styles.spanSlashSpacing} aria-hidden="true">/</span>}
                    </span>
                );
            })}
        </fieldset>
    );
}

export { ITextRadioGroupItem, ITextRadioGroupProps, TextRadioGroup };
